import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { NbDialogRef, NbLayoutScrollService, NbToastrService } from '@nebular/theme';
import { ShowableFormGroup } from '../../shared/ShowableFormGroup';
import { BSValidators } from '../../shared/bsvalidators';
import { Globals } from '../../shared/Globals';
import { SubscribedComponent } from '../../subscribed.component';
import { MarketVacancy, CreateManualVacancyPayload } from '../../interfaces/market-vacancy';
import { MarketVacancyService } from '../../shared/market-vacancy.service';
import { TranslateService } from '@ngx-translate/core';
import { IntakeMail, IntakeVacancyFields } from '../../interfaces/intake';
import { IntakeService, intakeErrorMessage } from '../../shared/intake.service';

function textOrNull(v: unknown): string | null {
  if (v === null || v === undefined) { return null; }
  const s = String(v).trim();
  return s ? s : null;
}

/** Form values to the intake request body: trimmed strings, empty as null, ISO dates. */
export function intakeFieldsFromForm(values: any): IntakeVacancyFields {
  return {
    title: textOrNull(values.title),
    company: textOrNull(values.company),
    location: textOrNull(values.location),
    description: textOrNull(values.description),
    deadline: values.deadline ? Globals.toISODate(values.deadline) : null,
    start_date: values.start_date ? Globals.toISODate(values.start_date) : null,
    hours_per_week: textOrNull(values.hours_per_week),
    tariff: textOrNull(values.tariff),
  };
}

@Component({
    selector: 'bst-create-vacancy-dialog',
    templateUrl: './create-vacancy-dialog.component.html',
    styleUrls: ['./create-vacancy-dialog.component.scss'],
    standalone: false
})
export class CreateVacancyDialogComponent extends SubscribedComponent implements OnInit {

  public forms: ShowableFormGroup[] = [];
  public submitting = false;
  public availableSources: string[] = [];
  public createdVacancy: MarketVacancy | null = null;
  public filling = false;
  public privacyExpanded = false;
  /** Set through the dialog context by the Intake verdict panel. null: the normal manual create. */
  @Input() intakeMode: 'create' | 'edit' | null = null;
  @Input() intakeMailId: number | null = null;
  /** Intake-only layout and DD-MM-YYYY pickers; the Market wizard keeps its own. */
  @HostBinding('class.intake') get intakeHost(): boolean { return !!this.intakeMode; }
  @Input() initialValues: Partial<IntakeVacancyFields> | null = null;
  public errorMessage: string | null = null;

  constructor(
    private marketVacancyService: MarketVacancyService,
    private router: Router,
    private fb: FormBuilder,
    public scroll: NbLayoutScrollService,
    private toastr: NbToastrService,
    private dialogRef: NbDialogRef<CreateVacancyDialogComponent>,
    private intake: IntakeService,
    private translate: TranslateService
  ) {
    super();
  }

  public close(): void {
    this.dialogRef.close(this.createdVacancy);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    if (!this.intakeMode) {
      this.loadSources();
    }

    // Step 0: Basic info
    this.forms.push(new ShowableFormGroup(this.fb.group({
      title: [null, [BSValidators.required, BSValidators.maxLength(255)]],
      source: [null],
      description: [null, [BSValidators.maxLength(30000)]],
      company: [null, [BSValidators.maxLength(255)]],
      location: [null, [BSValidators.maxLength(255)]],
      url: [null],
    })));

    // Step 1: Timeline
    this.forms.push(new ShowableFormGroup(this.fb.group({
      start_date: [null],
      end_date: [null],
      deadline: [null],
      hours_per_week: [null],
      tariff: [null],
      years_experience: [null],
    })));

    // Step 2: Hard Skills
    this.forms.push(new ShowableFormGroup(this.fb.group({
      skills: [null],
    })));

    // Step 3: Soft Skills
    this.forms.push(new ShowableFormGroup(this.fb.group({
      developments: [null],
    })));

    // Step 4: Certifications
    this.forms.push(new ShowableFormGroup(this.fb.group({
      certifications: [null],
    })));

    // Step 5: Industries
    this.forms.push(new ShowableFormGroup(this.fb.group({
      industries: [null],
    })));

    if (this.initialValues) {
      const v = this.initialValues;
      this.forms[0].form.patchValue({
        title: v.title ?? null,
        description: v.description ?? null,
        company: v.company ?? null,
        location: v.location ?? null,
      });
      this.forms[1].form.patchValue({
        deadline: v.deadline ? Globals.fromDateStringToMoment(v.deadline) : null,
        start_date: v.start_date ? Globals.fromDateStringToMoment(v.start_date) : null,
        hours_per_week: v.hours_per_week ?? null,
        tariff: v.tariff ?? null,
      });
    }
  }

  private async loadSources(): Promise<void> {
    try {
      const stats = await this.marketVacancyService.getStatistics();
      if (stats?.by_source) {
        // 'intake' is reserved (plan A NotIntakeSource): never suggest it for a manual vacancy.
        this.availableSources = Object.keys(stats.by_source).filter(s => s.trim().toLowerCase() !== 'intake').sort();
      }
    } catch (error) {
      console.error('Failed to load vacancy sources for autocomplete', error);
    }
  }

  public async submit(): Promise<void> {
    if (this.submitting) return;
    if (this.intakeMode) {
      await this.submitIntake();
      return;
    }
    this.submitting = true;

    try {
      const values: any = {};
      this.forms.forEach(f => Object.assign(values, f.form.value));

      const payload: CreateManualVacancyPayload = {
        title:            values.title,
        description:      values.description || undefined,
        source:           values.source || undefined,
        company:          values.company || undefined,
        location:         values.location || undefined,
        url:              values.url || undefined,
        years_experience: values.years_experience || undefined,
        hours_per_week:   values.hours_per_week || undefined,
        tariff:           values.tariff || undefined,
        start_date:       values.start_date ? Globals.toISODate(values.start_date) : undefined,
        end_date:         values.end_date ? Globals.toISODate(values.end_date) : undefined,
        deadline:         values.deadline ? Globals.toISODate(values.deadline) : undefined,
        skill_ids:        (values.skills || []).map((s: any) => s.id),
        certification_ids:(values.certifications || []).map((c: any) => c.id),
        development_ids:  (values.developments || []).map((d: any) => d.id),
        industry_ids:     (values.industries || []).map((i: any) => i.id),
      };

      this.createdVacancy = await this.marketVacancyService.addMarketVacancy(payload as any, [422]);
    } catch (error: any) {
      console.error('Failed to create vacancy', error);
      const message = error?.error?.errors?.source?.[0] === 'source_reserved'
        ? this.translate.instant('intake.form.source_reserved')
        : error?.error?.message || 'Failed to create vacancy. Please try again.';
      this.toastr.danger(message, 'Error');
    } finally {
      this.submitting = false;
    }
  }

  /** The title is the only required intake field; D1 answers a blank one with raw English. */
  get intakeInvalid(): boolean {
    return !!this.forms?.[0]?.form.invalid;
  }

  private async submitIntake(): Promise<void> {
    if (!this.intakeMailId || this.intakeInvalid) { return; }
    this.submitting = true;
    this.errorMessage = null;
    try {
      const values: any = {};
      this.forms.slice(0, 2).forEach(f => Object.assign(values, f.form.value));
      const fields = intakeFieldsFromForm(values);
      const call = this.intakeMode === 'create'
        ? this.intake.toDirectRequest(this.intakeMailId, fields)
        : this.intake.updateVacancy(this.intakeMailId, fields);
      const mail: IntakeMail = await call.catch(e => {
        // 409 (state_changed) and 422 are silent in IntakeService: show them inline. Other statuses were toasted.
        if (e?.status === 409 && e?.error?.message === 'intake.errors.state_changed') {
          this.dialogRef.close({ stateChanged: true });
        } else if (e?.status === 422 && !String(e?.error?.message ?? '').startsWith('intake.errors.')) {
          // Laravel validation text is English only; keep it out of the dialog.
          this.errorMessage = this.translate.instant('intake.errors.invalid_fields');
        } else if (e?.status === 409 || e?.status === 422) {
          this.errorMessage = intakeErrorMessage(this.translate, e);
        }
        return null;
      });
      if (!mail) { return; }
      this.dialogRef.close(mail);
    } finally {
      this.submitting = false;
    }
  }

  public async fillWithAI(): Promise<void> {
    if (this.filling) return;
    this.filling = true;
    try {
      const basicInfo = this.forms[0].form.value;
      const suggestions = await this.marketVacancyService.suggestFromDescription(
        basicInfo.title,
        basicInfo.description,
        basicInfo.company
      );
      if (suggestions) {
        const merge = (control: any, items: any[]) => {
          const current = control?.value || [];
          const currentIds = new Set(current.map((x: any) => x.id));
          control?.setValue([...current, ...items.filter((x: any) => !currentIds.has(x.id))]);
        };
        if (suggestions.skills?.length) merge(this.forms[2].form.get('skills'), suggestions.skills);
        if (suggestions.developments?.length) merge(this.forms[3].form.get('developments'), suggestions.developments);
        if (suggestions.certifications?.length) merge(this.forms[4].form.get('certifications'), suggestions.certifications);
        if (suggestions.industries?.length) merge(this.forms[5].form.get('industries'), suggestions.industries);
        if (suggestions.years_experience && !this.forms[1].form.get('years_experience')?.value) {
          this.forms[1].form.get('years_experience')?.setValue(suggestions.years_experience);
        }
        if (suggestions.hours_per_week && !this.forms[1].form.get('hours_per_week')?.value) {
          this.forms[1].form.get('hours_per_week')?.setValue(suggestions.hours_per_week);
        }
      }
    } catch (error: any) {
      console.error('Failed to get AI suggestions', error);
      const message = error?.error?.message || 'Could not get AI suggestions. Please try again.';
      this.toastr.warning(message, 'AI Fill');
    } finally {
      this.filling = false;
    }
  }

  public viewCreated(): void {
    if (this.createdVacancy) {
      const id = this.createdVacancy.id;
      this.dialogRef.close(this.createdVacancy);
      this.router.navigate(['/market/vacancies', id]);
    }
  }
}
